import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

export type OperationType =
  | 'import-csv'
  | 'import-crm'
  | 'manual-entry'
  | 'enrichment'
  | 'smart-columns'
  | 'prospecting'
  // The import-time profile fill (title / location / seniority). It is a
  // server-side Temporal run with no enrichment session behind it, so it is
  // driven ENTIRELY by websocket frames — see the reaper, which skips it.
  | 'profile-fill';

export type OperationStatus =
  | 'running'
  | 'pausing'
  | 'paused'
  | 'completed'
  | 'failed';

export interface OperationProgress {
  total: number;
  processed: number;
  failed: number;
}

export interface ActiveOperation {
  id: string;
  type: OperationType;
  label: string;
  status: OperationStatus;
  progress: OperationProgress;
  startedAt: string;
  leadListId?: string;
  sessionId?: string;
  taskIds?: string[];
}

interface ActiveOperationsState {
  operations: Record<string, ActiveOperation>;
}

const initialState: ActiveOperationsState = {
  operations: {},
};

const activeOperationsSlice = createSlice({
  name: 'activeOperations',
  initialState,
  reducers: {
    addOperation: (state, action: PayloadAction<ActiveOperation>) => {
      state.operations[action.payload.id] = action.payload;
    },
    updateProgress: (
      state,
      action: PayloadAction<{
        id: string;
        progress: Partial<OperationProgress>;
      }>
    ) => {
      const operation = state.operations[action.payload.id];
      if (operation) {
        operation.progress = {
          ...operation.progress,
          ...action.payload.progress,
        };
      }
    },
    setStatus: (
      state,
      action: PayloadAction<{ id: string; status: OperationStatus }>
    ) => {
      const operation = state.operations[action.payload.id];
      if (operation) {
        operation.status = action.payload.status;
      }
    },
    removeOperation: (state, action: PayloadAction<string>) => {
      delete state.operations[action.payload];
    },
    clearAllOperations: (state) => {
      state.operations = {};
    },
  },
});

export const {
  addOperation,
  updateProgress,
  setStatus,
  removeOperation,
  clearAllOperations,
} = activeOperationsSlice.actions;

export const activeOperationsReducer = activeOperationsSlice.reducer;

// Memoized on the operations map: a plain Object.values() returned a new
// array on every store update, so each subscriber (the leads table among
// them) re-rendered on every Redux action, e.g. every refetch during a
// 500-row enrichment.
const selectOperationsMap = (state: {
  activeOperations: ActiveOperationsState;
}) => state.activeOperations.operations;

export const selectAllOperations = createSelector(
  [selectOperationsMap],
  (operations) => Object.values(operations)
);

export const selectActiveOperations = createSelector(
  [selectAllOperations],
  (operations) =>
    operations.filter(
      (op) => op.status === 'running' || op.status === 'pausing'
    )
);

export const selectOperationById = (
  state: { activeOperations: ActiveOperationsState },
  id: string
) => state.activeOperations.operations[id];
